import { useCallback, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

import { UseColumnResizingReturn } from '../types'

interface UseColumnResizingProps {
  initialColumnWidths?: Record<string, number>
  onColumnResize?: (columnId: string, width: number) => void
  onColumnResizeEnd?: (columnId: string, width: number) => void
  syncInitialColumnWidths?: boolean
}

export function useColumnResizing({
  initialColumnWidths = {},
  onColumnResize,
  onColumnResizeEnd,
  syncInitialColumnWidths = false,
}: UseColumnResizingProps = {}): UseColumnResizingReturn {
  const [columnWidths, setColumnWidths] =
    useState<Record<string, number>>(initialColumnWidths)
  const [isActivelyResizing, setIsActivelyResizing] = useState(false)

  // Track which column is being resized and its pending width
  const [, setResizingColumn] = useState<string | null>(null)
  const [, setPendingWidth] = useState<number | null>(null)

  // Use refs to avoid stale closure issues and reduce re-renders
  // RAF-based throttle for smooth visual updates during resize
  const rafIdRef = useRef<number | null>(null)
  const pendingResizeRef = useRef<{ columnId: string; width: number } | null>(
    null,
  )
  const committedWidthOverridesRef = useRef<Record<string, number>>({})

  // Handle column resizing - throttled visual updates during drag
  const handleColumnResize = useCallback(
    (columnId: string, newWidth: number) => {
      // Enforce minimum and maximum widths
      const minColumnWidth = 30
      const maxColumnWidth = 800
      const adjustedWidth = Math.max(
        minColumnWidth,
        Math.min(newWidth, maxColumnWidth),
      )

      // Store pending resize for RAF processing
      pendingResizeRef.current = { columnId, width: adjustedWidth }

      // Track which column is being resized
      setIsActivelyResizing(true)

      // Use RAF to throttle visual updates for smooth 60fps performance
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(() => {
          const pending = pendingResizeRef.current
          if (pending) {
            setColumnWidths(prev => ({
              ...prev,
              [pending.columnId]: pending.width,
            }))
            setResizingColumn(pending.columnId)
            setPendingWidth(pending.width)
            setIsActivelyResizing(true)
          }
          rafIdRef.current = null
        })
      }
    },
    [],
  )

  const handleColumnResizeEnd = useCallback(
    (columnId: string, width: number) => {
      const minColumnWidth = 30
      const maxColumnWidth = 800
      const adjustedWidth = Math.max(
        minColumnWidth,
        Math.min(width, maxColumnWidth),
      )

      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }

      pendingResizeRef.current = null
      committedWidthOverridesRef.current = {
        ...committedWidthOverridesRef.current,
        [columnId]: adjustedWidth,
      }
      flushSync(() => {
        setColumnWidths(prev => ({
          ...prev,
          [columnId]: adjustedWidth,
        }))
        setResizingColumn(null)
        setPendingWidth(null)
        setIsActivelyResizing(false)
      })
      onColumnResizeEnd?.(columnId, adjustedWidth)
    },
    [onColumnResizeEnd],
  )

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
      }
    }
  }, [])

  // Reset column widths
  const resetColumnWidths = useCallback(() => {
    committedWidthOverridesRef.current = {}
    setColumnWidths({})
    setResizingColumn(null)
    setPendingWidth(null)
    if (onColumnResize) {
      // Notify parent that widths have been reset
      Object.keys(columnWidths).forEach(columnId => {
        onColumnResize(columnId, 0) // 0 indicates reset to default
      })
    }
  }, [columnWidths, onColumnResize])

  // Use ref to track previous initial widths for comparison
  const prevInitialWidthsRef = useRef<string>('')

  // Update column widths when initial widths change (using stable comparison)
  useEffect(() => {
    if (!initialColumnWidths || Object.keys(initialColumnWidths).length === 0)
      return

    const committedOverrides = committedWidthOverridesRef.current
    const effectiveInitialColumnWidths = { ...initialColumnWidths }

    if (syncInitialColumnWidths) {
      // Portal tables share a single optimistic project-level width record.
      // Treat incoming widths as authoritative so another expanded table can
      // replace this table's previously committed local resize.
      committedWidthOverridesRef.current = {}
    } else {
      // Always overlay committed overrides — they are the user's in-session
      // resizes and must win even if `initialColumnWidths` briefly reflects
      // stale data while an optimistic / debounced save is in flight.
      // Clearing them when they happen to match `initialColumnWidths` opens a
      // race: a later render with stale prefs would drop the column back to
      // its default width before the save settles, producing a visible snap.
      Object.entries(committedOverrides).forEach(([columnId, width]) => {
        effectiveInitialColumnWidths[columnId] = width
      })
    }

    // Create stable string representation for comparison
    const widthsKey = Object.entries(effectiveInitialColumnWidths)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}:${v}`)
      .join(',')

    // Only update if widths actually changed
    if (widthsKey !== prevInitialWidthsRef.current) {
      prevInitialWidthsRef.current = widthsKey
      setColumnWidths(effectiveInitialColumnWidths)
    }
  }, [initialColumnWidths, syncInitialColumnWidths])

  return {
    columnWidths,
    isActivelyResizing,
    handleColumnResize,
    handleColumnResizeEnd,
    resetColumnWidths,
  }
}
